/**
 * Simulação de 24 h da rede no navegador (epanet-js, MIT) e telemetria simulada derivada — Nexora Grid (US-405; nasceu na S3 v2).
 *
 * Criado em: 16/10/2026 · Última modificação: 18/10/2026 · Versão: 1.0.1
 *
 * A rede importada do DWG ainda não tem telemetria nem calibração. Para o 3D mostrar a água andando e os dados
 * do setor, a simulação hidráulica (a mesma da S1) dá, hora a hora, a vazão e a velocidade de cada trecho e a
 * pressão de cada nó. Dela derivam os valores "de telemetria" marcados como SIMULAÇÃO:
 * - nível dos reservatórios: a entrada é a média do dia; o nível sobe na madrugada e desce no pico;
 * - vazão dos macromedidores: vazão do trecho mais próximo;
 * - bombas e poços: ligados quando o reservatório do setor está abaixo de 85% (histerese até 95%).
 * Pressões sem calibração são indicativas (documento 13 §15).
 */
import { CountType, InitHydOption, LinkProperty, NodeProperty, NodeType, Project, Workspace } from "epanet-js";
import { NBR_12218 } from "../cco/medidas";

// NBR 12218: uma definição só no frontend (cco/medidas.ts), usada também pelo alerta dos cards do CCO (D-009)
export const NBR_MIN = NBR_12218.minima;
export const NBR_MAX = NBR_12218.maxima;

export type Simulacao = {
  horas: number;
  /** vazão (L/s, sinal = sentido de→para) por trecho e hora: vazoes[h].get(idTrecho) */
  vazoes: Map<string, number>[];
  velocidades: Map<string, number>[];
  pressoes: Map<string, number>[];
  /** vazão de saída de cada reservatório (L/s) por hora */
  saidaReservatorio: Map<string, number>[];
  tempoMs: number;
};

// uma carga só do motor (WASM), mesmo com chamadas simultâneas (o StrictMode do React roda efeitos duas vezes)
let carga: Promise<Workspace> | null = null;

/** Roda a simulação de período estendido e devolve os resultados por hora. */
export async function simular(textoInp: string): Promise<Simulacao> {
  if (!carga) carga = (async () => { const w = new Workspace(); await w.loadModule(); return w; })();
  const espaco = await carga;
  const inicio = performance.now();
  espaco.writeFile("rede.inp", textoInp);
  const modelo = new Project(espaco);
  modelo.open("rede.inp", "rede.rpt", "");
  const nNos = modelo.getCount(CountType.NodeCount);
  const nTrechos = modelo.getCount(CountType.LinkCount);
  const idsNos = Array.from({ length: nNos }, (_, i) => modelo.getNodeId(i + 1));
  const tiposNos = Array.from({ length: nNos }, (_, i) => modelo.getNodeType(i + 1));
  const idsTrechos = Array.from({ length: nTrechos }, (_, i) => modelo.getLinkId(i + 1));
  const extremos = Array.from({ length: nTrechos }, (_, i) => modelo.getLinkNodes(i + 1));
  const resultado: Simulacao = { horas: 0, vazoes: [], velocidades: [], pressoes: [], saidaReservatorio: [], tempoMs: 0 };
  modelo.openH();
  modelo.initH(InitHydOption.NoSave);
  let passo: number;
  do {
    const t = modelo.runH();
    if (t % 3600 === 0 && resultado.vazoes.length < 24) {
      const vazoes = new Map<string, number>();
      const velocidades = new Map<string, number>();
      const pressoes = new Map<string, number>();
      const saida = new Map<string, number>();
      for (let i = 0; i < nTrechos; i++) {
        const q = modelo.getLinkValue(i + 1, LinkProperty.Flow);
        vazoes.set(idsTrechos[i], q);
        velocidades.set(idsTrechos[i], modelo.getLinkValue(i + 1, LinkProperty.Velocity));
        const { node1, node2 } = extremos[i];
        // vazão que sai do reservatório pelo trecho de ligação
        if (tiposNos[node1 - 1] === NodeType.Reservoir) saida.set(idsNos[node1 - 1], (saida.get(idsNos[node1 - 1]) ?? 0) + q);
        if (tiposNos[node2 - 1] === NodeType.Reservoir) saida.set(idsNos[node2 - 1], (saida.get(idsNos[node2 - 1]) ?? 0) - q);
      }
      for (let i = 0; i < nNos; i++) {
        if (tiposNos[i] === NodeType.Junction) pressoes.set(idsNos[i], modelo.getNodeValue(i + 1, NodeProperty.Pressure));
      }
      resultado.vazoes.push(vazoes);
      resultado.velocidades.push(velocidades);
      resultado.pressoes.push(pressoes);
      resultado.saidaReservatorio.push(saida);
    }
    passo = modelo.nextH();
  } while (passo > 0);
  modelo.closeH();
  modelo.close();
  resultado.horas = resultado.vazoes.length;
  resultado.tempoMs = performance.now() - inicio;
  return resultado;
}

/** Interpola um valor por hora fracionária (laço de 24 h). */
export function interpolar(serie: number[], hora: number): number {
  const n = serie.length;
  const h0 = Math.floor(hora) % n;
  const f = hora - Math.floor(hora);
  return serie[h0] * (1 - f) + serie[(h0 + 1) % n] * f;
}

/**
 * Nível (%) do reservatório hora a hora: entrada constante igual à média do dia; o saldo acumulado
 * (entrada − saída) move o nível em torno de 70% conforme o volume útil.
 */
export function nivelReservatorio(sim: Simulacao, id: string, volumeM3: number): number[] {
  const saidas = sim.saidaReservatorio.map((s) => Math.max(0, s.get(id) ?? 0));
  const media = saidas.reduce((a, b) => a + b, 0) / Math.max(1, saidas.length);
  let saldo = 0;
  const brutos = saidas.map((q) => {
    saldo += ((media - q) * 3600) / 1000; // m³ na hora
    return saldo;
  });
  const meio = (Math.max(...brutos) + Math.min(...brutos)) / 2;
  return brutos.map((s) => Math.min(98, Math.max(8, 70 + ((s - meio) / Math.max(volumeM3, 1)) * 100)));
}

/** Estado simulado de uma bomba pelo nível do reservatório (liga < 85%, desliga > 95%). */
export function estadoBomba(niveis: number[]): boolean[] {
  let ligada = true;
  return niveis.map((n) => {
    if (n < 85) ligada = true;
    else if (n > 95) ligada = false;
    return ligada;
  });
}

/** Pressão mínima, média, máxima e % de nós dentro da faixa da NBR 12218 num conjunto de nós. */
export function resumoPressao(pressoes: Map<string, number>, nos: Iterable<string>) {
  let min = Infinity, max = -Infinity, soma = 0, n = 0, dentro = 0;
  for (const id of nos) {
    const p = pressoes.get(id);
    if (p === undefined) continue;
    min = Math.min(min, p); max = Math.max(max, p); soma += p; n++;
    if (p >= NBR_MIN && p <= NBR_MAX) dentro++;
  }
  return n ? { min, max, media: soma / n, dentroPct: (dentro / n) * 100, nos: n } : null;
}
